import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  useAplicaciones,
  useCreateAplicacion,
  useUpdateAplicacion,
  useDeleteAplicacion,
} from '../../hooks/useAplicaciones';
import type { AplicacionDTO } from '../../hooks/useAplicaciones';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';
import { useAuthStore } from '../../stores/auth-store';

// ── Edit Modal ─────────────────────────────────────────────────────────
function EditAplicacionModal({
  aplicacion,
  onClose,
}: {
  aplicacion: AplicacionDTO;
  onClose: () => void;
}) {
  const updateMutation = useUpdateAplicacion();
  const [nombre, setNombre] = useState(aplicacion.nombre);

  const handleSubmit = async () => {
    const trimmed = nombre.trim();
    if (!trimmed) {
      toast('error', 'El nombre es requerido');
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: aplicacion.id, nombre: trimmed });
      onClose();
    } catch {
      // interceptor shows toast
    }
  };

  return (
    <Modal isOpen title="Editar Categoría" onClose={onClose} size="sm">
      <div className="space-y-4">
        <Input
          label="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="Ej: Cielo raso"
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={updateMutation.isPending}>
            Guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Delete Confirmation Modal ──────────────────────────────────────────
function DeleteAplicacionModal({
  aplicacion,
  onClose,
}: {
  aplicacion: AplicacionDTO;
  onClose: () => void;
}) {
  const deleteMutation = useDeleteAplicacion();

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(aplicacion.id);
      onClose();
    } catch {
      // 409 interceptor shows server message (in use)
    }
  };

  return (
    <Modal isOpen title="Eliminar Categoría" onClose={onClose} size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          ¿Está seguro que desea eliminar la categoría{' '}
          <span className="font-semibold">"{aplicacion.nombre}"</span>?
        </p>
        <p className="text-xs text-gray-500">
          Si la categoría está siendo utilizada en proformas, la eliminación será bloqueada.
        </p>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            isLoading={deleteMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            Eliminar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main Tab Component ─────────────────────────────────────────────────
export function AplicacionesTab() {
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const { data: aplicaciones, isLoading } = useAplicaciones();
  const createAplicacion = useCreateAplicacion();
  const [newNombre, setNewNombre] = useState('');
  const [editingAplicacion, setEditingAplicacion] = useState<AplicacionDTO | null>(null);
  const [deletingAplicacion, setDeletingAplicacion] = useState<AplicacionDTO | null>(null);

  const handleCreate = async () => {
    if (!newNombre.trim()) {
      toast('error', 'El nombre es requerido');
      return;
    }
    try {
      await createAplicacion.mutateAsync({ nombre: newNombre.trim() });
      setNewNombre('');
    } catch {
      // hook handles toast
    }
  };

  if (isLoading) {
    return <Card className="p-4"><Skeleton className="h-40 w-full" /></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Tipos de Categoría</h3>

          {/* List */}
          <div className="space-y-2 mb-4">
            {(aplicaciones ?? []).map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between rounded-lg border border-gray-100 px-4 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">{app.nombre}</span>
                  <span className={`text-xs ${app.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                    {app.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingAplicacion(app)}
                      className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Editar categoría"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setDeletingAplicacion(app)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Eliminar categoría"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {(aplicaciones ?? []).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No hay categorías registradas</p>
            )}
          </div>

          {/* Add new */}
          {canEdit && (
            <div className="flex items-end gap-3 border-t border-gray-100 pt-4">
              <div className="flex-1">
                <Input
                  label="Nueva categoría"
                  value={newNombre}
                  onChange={(e) => setNewNombre(e.target.value)}
                  placeholder="Ej: Cielo raso"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreate}
                isLoading={createAplicacion.isPending}
                leftIcon={<Plus size={14} />}
              >
                Agregar
              </Button>
            </div>
          )}
        </div>
      </Card>

      {editingAplicacion && (
        <EditAplicacionModal
          aplicacion={editingAplicacion}
          onClose={() => setEditingAplicacion(null)}
        />
      )}
      {deletingAplicacion && (
        <DeleteAplicacionModal
          aplicacion={deletingAplicacion}
          onClose={() => setDeletingAplicacion(null)}
        />
      )}
    </div>
  );
}
